import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  AccessTime as AccessTimeIcon,
  DevicesOther as DevicesIcon,
  Smartphone as SmartphoneIcon,
  Laptop as LaptopIcon,
  Tablet as TabletIcon,
  DesktopWindows as DesktopIcon,
  Tv as TvIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { styled, keyframes } from '@mui/material/styles';

// Styled components with app colors
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  height: '100%',
  border: '1px solid rgba(0, 105, 92, 0.1)'
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 500,
  '&.MuiChip-colorPrimary': {
    backgroundColor: '#006938',
    color: '#ffffff'
  },
  '&.MuiChip-outlined': {
    borderColor: '#006938',
    color: '#006938'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#006938',
  '&:hover': {
    backgroundColor: '#004d2b',
  },
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600
}));

// Keyframes for animations
const cubeRotate = keyframes`
  0% { transform: rotateX(0) rotateY(0) rotateZ(0); }
  100% { transform: rotateX(360deg) rotateY(360deg) rotateZ(360deg); }
`;

const cubePulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const cubeFloat = keyframes`
  0%, 100% { transform: translateY(0) rotateX(45deg) rotateY(45deg); }
  50% { transform: translateY(-15px) rotateX(45deg) rotateY(45deg); }
`;

// Styled components
const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #007C3F 30%, #00A651 90%)',
  padding: theme.spacing(6, 0),
  marginBottom: theme.spacing(4),
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.1)',
    opacity: 0.2,
    zIndex: 1,
  }
}));

const CubeContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  perspective: '1000px',
  zIndex: 0,
  overflow: 'hidden',
}));

const Cube = styled(Box)(({ size = 60, posX, posY, duration = 20, delay = 0, color = '#000' }) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  left: `${posX}%`,
  top: `${posY}%`,
  transformStyle: 'preserve-3d',
  animation: `${cubeRotate} ${duration}s infinite linear`,
  animationDelay: `${delay}s`,
  opacity: 0.7,
  '& > div': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: color,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
  }
}));

const CubeFace = styled(Box)(({ transform }) => ({
  transform,
}));

const FloatingCube = styled(Box)(({ size = 80, posX, posY, duration = 5, delay = 0 }) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  left: `${posX}%`,
  top: `${posY}%`,
  animation: `${cubeFloat} ${duration}s infinite ease-in-out`,
  animationDelay: `${delay}s`,
  transformStyle: 'preserve-3d',
  transform: 'rotateX(45deg) rotateY(45deg)',
  '& > div': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: '#000',
    boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 227, 2, 0.3)',
  }
}));

const PulsingCube = styled(Box)(({ size = 100, posX, posY }) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  left: `${posX}%`,
  top: `${posY}%`,
  animation: `${cubePulse} 4s infinite ease-in-out`,
  transformStyle: 'preserve-3d',
  transform: 'rotateX(45deg) rotateY(45deg)',
  '& > div': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: '#000',
    boxShadow: '0 0 30px rgba(0, 0, 0, 0.4)',
    border: '2px solid rgba(255, 227, 2, 0.5)',
  }
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(4),
  marginTop: theme.spacing(2),
}));

const StatBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  minWidth: 120,
  textAlign: 'center',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(5px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#fff',
}));

const Issues = () => {
  const [totalIssues, setTotalIssues] = useState(0);
  const [resolvedIssues, setResolvedIssues] = useState(0);
  const [pendingIssues, setPendingIssues] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: issues } = await supabase
          .from('issues')
          .select('status');
        
        if (issues) {
          setTotalIssues(issues.length);
          setResolvedIssues(issues.filter(issue => issue.status === 'resolved').length);
          setPendingIssues(issues.filter(issue => issue.status === 'pending').length);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    
    fetchStats();
  }, []);
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deviceCategoryId: '',
    deviceBrandId: '',
    priority: 'medium'
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get icon based on category name
  const getCategoryIcon = (categoryName) => {
    switch (categoryName.toLowerCase()) {
      case 'smartphone':
        return <SmartphoneIcon />;
      case 'laptop':
        return <LaptopIcon />;
      case 'tablet':
        return <TabletIcon />;
      case 'desktop':
        return <DesktopIcon />;
      case 'tv':
        return <TvIcon />;
      default:
        return <DevicesIcon />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('device_categories')
          .select('*');
        
        if (categoriesError) throw categoriesError;
        setCategories(categoriesData || []);

        // Fetch brands
        const { data: brandsData, error: brandsError } = await supabase
          .from('device_brands')
          .select('*');
        
        if (brandsError) throw brandsError;
        setBrands(brandsData || []);

        // Fetch issues with related data
        const { data: issuesData, error: issuesError } = await supabase
          .from('user_issues')
          .select(`
            *,
            device_categories:device_category_id(name),
            device_brands:device_brand_id(name),
            issue_responses:id(count)
          `)
          .order('created_at', { ascending: false });
        
        if (issuesError) throw issuesError;
        setIssues(issuesData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading data. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter brands when category changes
  useEffect(() => {
    if (selectedCategory) {
      const filtered = brands.filter(brand => brand.category_id === parseInt(selectedCategory));
      setFilteredBrands(filtered);
      setSelectedBrand('');
    } else {
      setFilteredBrands(brands);
    }
  }, [selectedCategory, brands]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Update filtered brands when category changes in form
    if (name === 'deviceCategoryId') {
      const filtered = brands.filter(brand => brand.category_id === parseInt(value));
      setFilteredBrands(filtered);
      setFormData(prev => ({
        ...prev,
        deviceBrandId: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Insert new issue without requiring user authentication
      const { data, error } = await supabase
        .from('user_issues')
        .insert([
          {
            title: formData.title,
            description: formData.description,
            device_category_id: parseInt(formData.deviceCategoryId),
            device_brand_id: parseInt(formData.deviceBrandId),
            priority: formData.priority
          }
        ])
        .select();

      if (error) throw error;

      // Refresh issues list
      const { data: updatedIssues, error: fetchError } = await supabase
        .from('user_issues')
        .select(`
          *,
          device_categories:device_category_id(name),
          device_brands:device_brand_id(name),
          issue_responses:id(count)
        `)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      setIssues(updatedIssues || []);
      setOpenForm(false);
      setFormData({
        title: '',
        description: '',
        deviceCategoryId: '',
        deviceBrandId: '',
        priority: 'medium'
      });
      
      setSnackbar({
        open: true,
        message: 'Issue submitted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error submitting issue:', error);
      setSnackbar({
        open: true,
        message: 'Error submitting issue. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search and filtering
  const filteredIssues = issues.filter(issue => {
    const matchesSearch = issue.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          issue.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory ? issue.device_category_id === parseInt(selectedCategory) : true;
    const matchesBrand = selectedBrand ? issue.device_brand_id === parseInt(selectedBrand) : true;
    
    return matchesSearch && matchesCategory && matchesBrand;
  });

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // View issue details
  const handleViewIssue = (issueId) => {
    navigate(`/issues/view/${issueId}`);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <HeaderBox>
        <CubeContainer>
          {/* Large central cube */}
          <PulsingCube posX={50} posY={50} size={120}>
            <CubeFace transform="translateZ(60px)" />
            <CubeFace transform="rotateX(180deg) translateZ(60px)" />
            <CubeFace transform="rotateY(-90deg) translateZ(60px)" />
            <CubeFace transform="rotateY(90deg) translateZ(60px)" />
            <CubeFace transform="rotateX(90deg) translateZ(60px)" />
            <CubeFace transform="rotateX(-90deg) translateZ(60px)" />
          </PulsingCube>
          
          {/* Floating cubes */}
          <FloatingCube posX={15} posY={20} size={60} delay={0.5}>
            <CubeFace transform="translateZ(30px)" />
            <CubeFace transform="rotateX(180deg) translateZ(30px)" />
            <CubeFace transform="rotateY(-90deg) translateZ(30px)" />
            <CubeFace transform="rotateY(90deg) translateZ(30px)" />
            <CubeFace transform="rotateX(90deg) translateZ(30px)" />
            <CubeFace transform="rotateX(-90deg) translateZ(30px)" />
          </FloatingCube>
          
          <FloatingCube posX={85} posY={25} size={50} delay={1.2}>
            <CubeFace transform="translateZ(25px)" />
            <CubeFace transform="rotateX(180deg) translateZ(25px)" />
            <CubeFace transform="rotateY(-90deg) translateZ(25px)" />
            <CubeFace transform="rotateY(90deg) translateZ(25px)" />
            <CubeFace transform="rotateX(90deg) translateZ(25px)" />
            <CubeFace transform="rotateX(-90deg) translateZ(25px)" />
          </FloatingCube>
          
          {/* Rotating cubes */}
          <Cube posX={10} posY={70} size={40} duration={15} delay={0}>
            <CubeFace transform="translateZ(20px)" />
            <CubeFace transform="rotateX(180deg) translateZ(20px)" />
            <CubeFace transform="rotateY(-90deg) translateZ(20px)" />
            <CubeFace transform="rotateY(90deg) translateZ(20px)" />
            <CubeFace transform="rotateX(90deg) translateZ(20px)" />
            <CubeFace transform="rotateX(-90deg) translateZ(20px)" />
          </Cube>
          
          <Cube posX={80} posY={75} size={30} duration={12} delay={2}>
            <CubeFace transform="translateZ(15px)" />
            <CubeFace transform="rotateX(180deg) translateZ(15px)" />
            <CubeFace transform="rotateY(-90deg) translateZ(15px)" />
            <CubeFace transform="rotateY(90deg) translateZ(15px)" />
            <CubeFace transform="rotateX(90deg) translateZ(15px)" />
            <CubeFace transform="rotateX(-90deg) translateZ(15px)" />
          </Cube>
          
          <Cube posX={30} posY={85} size={25} duration={18} delay={1}>
            <CubeFace transform="translateZ(12.5px)" />
            <CubeFace transform="rotateX(180deg) translateZ(12.5px)" />
            <CubeFace transform="rotateY(-90deg) translateZ(12.5px)" />
            <CubeFace transform="rotateY(90deg) translateZ(12.5px)" />
            <CubeFace transform="rotateX(90deg) translateZ(12.5px)" />
            <CubeFace transform="rotateX(-90deg) translateZ(12.5px)" />
          </Cube>
        </CubeContainer>
        <HeaderContent>
          <Typography variant="h3" component="h1" gutterBottom>
            Technical Issues Tracker
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Track and manage your technical issues efficiently
          </Typography>
          <StatsContainer>
            <StatBox elevation={0}>
              <Typography variant="h4">{totalIssues}</Typography>
              <Typography variant="body2">Total Issues</Typography>
            </StatBox>
            <StatBox elevation={0}>
              <Typography variant="h4">{resolvedIssues}</Typography>
              <Typography variant="body2">Resolved</Typography>
            </StatBox>
            <StatBox elevation={0}>
              <Typography variant="h4">{pendingIssues}</Typography>
              <Typography variant="body2">Pending</Typography>
            </StatBox>
          </StatsContainer>
        </HeaderContent>
      </HeaderBox>
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        fontWeight="bold"
        sx={{ 
          color: '#006938',
          borderBottom: '3px solid #006938',
          paddingBottom: 1,
          display: 'inline-block'
        }}
      >
        Device Issues & Solutions
      </Typography>
      
      <Grid container spacing={3}>
        {/* Search and Filters */}
        <Grid item xs={12}>
          <StyledPaper>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search issues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#006938',
                    },
                  },
                }}
              />
              
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Device Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Device Category"
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#006938',
                    },
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  label="Brand"
                  disabled={!selectedCategory && filteredBrands.length === 0}
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#006938',
                    },
                  }}
                >
                  <MenuItem value="">All Brands</MenuItem>
                  {filteredBrands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <StyledButton 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setOpenForm(!openForm)}
                sx={{ height: 56 }}
              >
                Post Issue
              </StyledButton>
            </Box>
            
            {/* Categories chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mr: 1, pt: 0.5, color: '#006938', fontWeight: 'bold' }}>
                Popular Categories:
              </Typography>
              {categories.slice(0, 5).map((category) => (
                <CategoryChip
                  key={category.id}
                  label={category.name}
                  icon={getCategoryIcon(category.name)}
                  onClick={() => setSelectedCategory(category.id.toString())}
                  color={selectedCategory === category.id.toString() ? "primary" : "default"}
                  variant={selectedCategory === category.id.toString() ? "filled" : "outlined"}
                />
              ))}
            </Box>
            
            {/* Submit issue form */}
            {openForm && (
              <Box 
                component="form" 
                onSubmit={handleSubmit} 
                sx={{ 
                  mt: 3, 
                  p: 3, 
                  bgcolor: 'background.paper', 
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0, 105, 92, 0.1)',
                  border: '1px solid rgba(0, 105, 92, 0.2)'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#006938', fontWeight: 'bold' }}>
                  Submit New Issue
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Issue Title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#006938',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#006938',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Device Category</InputLabel>
                      <Select
                        name="deviceCategoryId"
                        value={formData.deviceCategoryId}
                        onChange={handleInputChange}
                        label="Device Category"
                        sx={{
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#006938',
                          },
                        }}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required disabled={!formData.deviceCategoryId}>
                      <InputLabel>Brand</InputLabel>
                      <Select
                        name="deviceBrandId"
                        value={formData.deviceBrandId}
                        onChange={handleInputChange}
                        label="Brand"
                        sx={{
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#006938',
                          },
                        }}
                      >
                        {filteredBrands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={4}
                      label="Issue Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe your issue in detail. Include what you've already tried and any error messages you've received."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#006938',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#006938',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        label="Priority"
                        sx={{
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#006938',
                          },
                        }}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => setOpenForm(false)}
                      sx={{ 
                        borderColor: '#006938', 
                        color: '#006938',
                        '&:hover': {
                          borderColor: '#004d2b',
                          backgroundColor: 'rgba(0, 105, 92, 0.04)'
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <StyledButton 
                      type="submit" 
                      variant="contained" 
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Submit Issue'}
                    </StyledButton>
                  </Grid>
                </Grid>
              </Box>
            )}
          </StyledPaper>
        </Grid>
        
        {/* Issues Table */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom sx={{ color: '#006938', fontWeight: 'bold' }}>
              Recent Issues
            </Typography>
            
            {loading && !openForm ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress sx={{ color: '#006938' }} />
              </Box>
            ) : filteredIssues.length === 0 ? (
              <Alert severity="info" sx={{ backgroundColor: 'rgba(0, 105, 92, 0.08)', color: '#006938' }}>
                No issues found. Try adjusting your filters or post a new issue.
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'rgba(0, 105, 92, 0.08)' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Brand</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Responses</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#006938' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredIssues
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((issue) => (
                          <TableRow 
                            key={issue.id} 
                            hover 
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'rgba(0, 105, 92, 0.04)' 
                              } 
                            }}
                          >
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="medium">
                                {issue.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                                {issue.description.substring(0, 100)}...
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={getCategoryIcon(issue.device_categories?.name || '')} 
                                label={issue.device_categories?.name} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: 'rgba(0, 105, 92, 0.1)', 
                                  color: '#006938',
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>{issue.device_brands?.name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={issue.status} 
                                color={
                                  issue.status === 'solved' ? 'success' : 
                                  issue.status === 'in progress' ? 'warning' : 
                                  'default'
                                }
                                size="small"
                                sx={{ 
                                  fontWeight: 500,
                                  ...(issue.status === 'open' && {
                                    backgroundColor: 'rgba(0, 105, 92, 0.1)',
                                    color: '#006938'
                                  })
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {formatDate(issue.created_at)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={<CommentIcon />} 
                                label={issue.issue_responses?.[0]?.count || 0} 
                                size="small" 
                                variant="outlined"
                                sx={{ borderColor: '#006938', color: '#006938' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewIssue(issue.id)}
                                sx={{ 
                                  borderColor: '#006938', 
                                  color: '#006938',
                                  '&:hover': {
                                    borderColor: '#004d2b',
                                    backgroundColor: 'rgba(0, 105, 92, 0.04)'
                                  }
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredIssues.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    '.MuiTablePagination-select': {
                      color: '#006938'
                    },
                    '.MuiTablePagination-selectIcon': {
                      color: '#006938'
                    }
                  }}
                />
              </>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default Issues;